import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Plus, Upload } from 'lucide-react';
import { api, apiMultipart } from '../../lib/api.ts';
import { Aviso, Boton, BotonCirculo, Campo, EncabezadoPagina, Insignia } from '../../componentes/ui.tsx';

export interface Marca {
  id: string;
  codigoExterno: string;
  nombre: string;
  colorPrimario: string | null;
  colorSecundario: string | null;
  logoUrl: string | null;
  logoEsSvg: boolean;
  estado: 'ACTIVO' | 'INACTIVO';
}

export function slugDe(nombre: string): string {
  return nombre
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/** Sube el archivo a /admin/media y devuelve el assetId. */
export async function subirLogo(archivo: File): Promise<string> {
  const forma = new FormData();
  forma.append('tipo', 'IMAGEN');
  forma.append('archivo', archivo);
  const asset = await apiMultipart<{ assetId: string }>('/admin/media', forma);
  return asset.assetId;
}

/** Selector de color de marca: swatch redondo + hex editable, nada del sistema a la vista. */
export function CampoColor({
  etiqueta,
  valor,
  onCambiar,
}: {
  etiqueta: string;
  valor: string;
  onCambiar: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-principal">{etiqueta}</span>
      <div className="campo flex items-center gap-3">
        <span className="relative inline-block h-7 w-7 shrink-0 overflow-hidden rounded-full border border-black/10 shadow-inner" style={{ backgroundColor: valor }}>
          <input
            type="color"
            value={valor}
            onChange={(e) => onCambiar(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            aria-label={etiqueta}
          />
        </span>
        <input
          value={valor}
          onChange={(e) => onCambiar(e.target.value)}
          className="w-24 bg-transparent font-mono text-sm uppercase outline-none"
          maxLength={7}
          aria-label={`${etiqueta} (hex)`}
        />
      </div>
    </label>
  );
}

export function LogoMarca({ marca, className = 'h-16 w-16' }: { marca: Marca; className?: string }) {
  return marca.logoUrl ? (
    <img src={marca.logoUrl} alt={marca.nombre} className={`${className} rounded-2xl bg-white object-contain p-1.5 shadow-[var(--sombra)]`} />
  ) : (
    <span
      className={`${className} flex items-center justify-center rounded-2xl text-2xl font-medium text-white`}
      style={{ backgroundColor: marca.colorPrimario ?? 'var(--negro)' }}
    >
      {marca.nombre.charAt(0)}
    </span>
  );
}

export function PaginaAdminMarcas() {
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const marcas = useQuery({
    queryKey: ['admin-marcas'],
    queryFn: () => api<{ items: Marca[] }>('/admin/marcas'),
  });

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina
        titulo="Marcas"
        subtitulo={`${marcas.data?.items.length ?? 0} marcas`}
        acciones={
          <BotonCirculo aria-label="Nueva marca" className="h-11 w-11" onClick={() => setCreando(true)}>
            <Plus size={18} />
          </BotonCirculo>
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}

      <ul className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
        {marcas.data?.items.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => void navegar({ to: '/admin/marcas/$marcaId', params: { marcaId: m.id } })}
              className="tarjeta block w-full p-5 text-left transition hover:-translate-y-0.5 hover:shadow-[var(--sombra-alta)]"
            >
              <div className="flex items-start justify-between">
                <LogoMarca marca={m} />
                {m.estado !== 'ACTIVO' ? <Insignia tono="alerta">INACTIVA</Insignia> : null}
              </div>
              <p className="mt-4 text-[17px] font-medium">{m.nombre}</p>
              <p className="text-xs text-suave">{m.codigoExterno}</p>
              <div className="mt-3 flex items-center gap-1.5">
                {[m.colorPrimario, m.colorSecundario].filter(Boolean).map((c, i) => (
                  <span key={i} className="h-5 w-5 rounded-full border border-black/10" style={{ backgroundColor: c! }} />
                ))}
                {!m.colorPrimario && !m.colorSecundario ? (
                  <span className="text-xs text-suave">Sin colores</span>
                ) : null}
              </div>
            </button>
          </li>
        ))}
        {marcas.data?.items.length === 0 ? (
          <li className="tarjeta col-span-full p-6 text-sm text-suave">Sin marcas todavía. Cree una con el botón +.</li>
        ) : null}
      </ul>

      {creando ? (
        <ModalMarca
          alCerrar={() => setCreando(false)}
          alListo={async () => {
            setCreando(false);
            setError(null);
            await qc.invalidateQueries({ queryKey: ['admin-marcas'] });
          }}
          alFallar={(m) => setError(`Error: ${m}`)}
        />
      ) : null}
    </main>
  );
}

function ModalMarca({
  alCerrar,
  alListo,
  alFallar,
}: {
  alCerrar: () => void;
  alListo: () => Promise<void>;
  alFallar: (mensaje: string) => void;
}) {
  const [nombre, setNombre] = useState('');
  const [colorPrimario, setColorPrimario] = useState('#111225');
  const [colorSecundario, setColorSecundario] = useState('#d7f75b');
  const [logo, setLogo] = useState<File | null>(null);

  const crear = useMutation({
    mutationFn: async () => {
      const logoAssetId = logo ? await subirLogo(logo) : undefined;
      await api('/admin/marcas', {
        metodo: 'POST',
        cuerpo: {
          codigoExterno: slugDe(nombre.trim()),
          nombre: nombre.trim(),
          colorPrimario,
          colorSecundario,
          ...(logoAssetId ? { logoAssetId } : {}),
        },
      });
    },
    onSuccess: () => void alListo(),
    onError: (e) => {
      alFallar(e.message);
      alCerrar();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          crear.mutate();
        }}
        className="tarjeta w-full max-w-md space-y-3 p-6 shadow-[var(--sombra-alta)]"
      >
        <h2 className="text-[22px] font-light">Nueva marca</h2>
        <Campo etiqueta="Nombre de la marca" value={nombre} onChange={(e) => setNombre(e.target.value)} required autoFocus />

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-principal">Logo</span>
          <span className="campo flex cursor-pointer items-center gap-3">
            <Upload size={16} className="shrink-0 text-suave" />
            <span className={`truncate text-sm ${logo ? '' : 'text-suave'}`}>{logo ? logo.name : 'Elegir imagen…'}</span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => setLogo(e.target.files?.[0] ?? null)}
            />
          </span>
        </label>
        {logo ? <img src={URL.createObjectURL(logo)} alt="Vista previa" className="h-20 w-20 rounded-2xl bg-superficie2 object-contain p-2" /> : null}

        <div className="grid grid-cols-2 gap-3">
          <CampoColor etiqueta="Color principal" valor={colorPrimario} onCambiar={setColorPrimario} />
          <CampoColor etiqueta="Color secundario" valor={colorSecundario} onCambiar={setColorSecundario} />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Boton variante="secundario" type="button" onClick={alCerrar}>Cancelar</Boton>
          <Boton type="submit" disabled={crear.isPending || !nombre.trim()}>
            {crear.isPending ? 'Creando…' : 'Crear marca'}
          </Boton>
        </div>
      </form>
    </div>
  );
}
